import type { AppointmentType } from '../../types';

/** A single planned template-link write, with enough context to undo it. */
export interface LinkPlanItem {
  appointmentType: AppointmentType;
  /** template_id the type had before the commit; restored on undo. */
  previousTemplateId: string | null;
}

export interface LinkPlan {
  /** Types with no template today — linking is a pure addition. */
  additions: LinkPlanItem[];
  /** Types using a different template today — linking moves them off it. */
  reassignments: LinkPlanItem[];
}

/**
 * Partitions a selection of appointment types into additions vs reassignments
 * for linking to `templateId`. Types already linked to `templateId` are
 * dropped (no-op). This surface is additive/reassign-only: the forward plan
 * never contains a null template write.
 */
export const buildLinkPlan = (
  selectedTypes: AppointmentType[],
  templateId: string,
): LinkPlan => {
  const additions: LinkPlanItem[] = [];
  const reassignments: LinkPlanItem[] = [];

  for (const appointmentType of selectedTypes) {
    const previousTemplateId = appointmentType.template_id ?? null;
    if (previousTemplateId === templateId) {
      continue;
    }
    const item: LinkPlanItem = { appointmentType, previousTemplateId };
    if (previousTemplateId === null) {
      additions.push(item);
    } else {
      reassignments.push(item);
    }
  }

  return { additions, reassignments };
};

/** All writes in a plan, additions first. */
export const planItems = (plan: LinkPlan): LinkPlanItem[] => [
  ...plan.additions,
  ...plan.reassignments,
];
